import { useCallback, useMemo, useRef, useState } from 'react';
import { noop } from '@shared/utils/noop';
import { useEmailVerificationFlow } from './useEmailVerificationFlow';
import type { EmailVerificationDialogProps } from './EmailVerificationDialog.types';

type EnsureEmailVerified = () => Promise<boolean>;

/**
 * Gates an action (e.g. website publish) behind email verification.
 *
 * Call `ensureEmailVerified()` before the gated action: it resolves `true`
 * immediately when the email is already verified, otherwise it opens the
 * verification dialog and resolves `true` once the user confirms a verified
 * status, or `false` if they dismiss it. Render `<EmailVerificationDialog {...dialogProps} />`
 * somewhere in the consuming tree.
 *
 * The sending, cooldown and status-re-check rules live in `useEmailVerificationFlow`, shared with
 * the Go Live Checks wizard's verification step. This hook only adds the dialog around them: when
 * it's open, and the promise the caller awaits.
 */
export const useEmailVerificationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Resolves the promise returned by the active ensureEmailVerified() call.
  const resolveRef = useRef<(verified: boolean) => void>(noop);

  const settle = useCallback((verified: boolean) => {
    setIsOpen(false);
    resolveRef.current(verified);
    resolveRef.current = noop;
  }, []);

  const flow = useEmailVerificationFlow({ onVerified: () => settle(true) });
  const { isVerificationRequired, reset, cancel } = flow;

  const ensureEmailVerified = useCallback<EnsureEmailVerified>(() => {
    // When the feature flag is off (or the email is already verified), skip the gate entirely so
    // publishing proceeds as before.
    if (!isVerificationRequired) {
      return Promise.resolve(true);
    }
    reset();
    setIsOpen(true);
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
    });
  }, [isVerificationRequired, reset]);

  const handleClose = useCallback(() => {
    // Invalidate any in-flight verify attempt so a late resolution can't settle this session.
    cancel();
    settle(false);
  }, [cancel, settle]);

  const dialogProps = useMemo<EmailVerificationDialogProps>(
    () => ({
      isOpen,
      step: flow.step,
      email: flow.email,
      isSending: flow.isSending,
      isChecking: flow.isChecking,
      checkFailed: flow.checkFailed,
      checkErrored: flow.checkErrored,
      sendFailed: flow.sendFailed,
      didResend: flow.didResend,
      resendCooldown: flow.resendCooldown,
      onVerify: flow.handleVerify,
      onResend: flow.handleResend,
      onConfirmVerified: flow.handleConfirmVerified,
      onClose: handleClose,
    }),
    [
      isOpen,
      flow.step,
      flow.email,
      flow.isSending,
      flow.isChecking,
      flow.checkFailed,
      flow.checkErrored,
      flow.sendFailed,
      flow.didResend,
      flow.resendCooldown,
      flow.handleVerify,
      flow.handleResend,
      flow.handleConfirmVerified,
      handleClose,
    ],
  );

  return {
    ensureEmailVerified,
    dialogProps,
  };
};
